import { useLocalStorage } from '@vueuse/core'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { useAssessmentTestStore } from '@stores/assessment-test'
import { useKidStore } from '@stores/kid'

import { useAssessmentTestConfig } from '@hooks/assessments/assessment-test-config.hook'
import { useKidRoutes } from '@composables/routes/kid-route.hook'

import { getQuerySearchParam } from '@helpers/query'

interface AssessmentTestInfoReturn {
  initAssessmentTest: () => void
}

export const useAssessmentTestInfo = (): AssessmentTestInfoReturn => {
  const assessmentTestStore = useAssessmentTestStore()
  const kidStore = useKidStore()
  const { isRouteDisciplineEla } = useKidRoutes()

  const answersKey = computed(() => `assessment-test-answers-${kidStore.getActiveKid?.id}-${isRouteDisciplineEla.value ? 'ela' : 'math'}`)
  const stepKey = computed(() => `assessment-quiz-step-${kidStore.getActiveKid?.id}-${isRouteDisciplineEla.value ? 'ela' : 'math'}`)

  const answers = useLocalStorage<string | null>(answersKey.value, null)
  const step = useLocalStorage<string | null>(stepKey.value, null)

  const { configs } = useAssessmentTestConfig()
  const isComprehension = getQuerySearchParam('comprehension_check_up') === '1'
  const grade = computed(() => kidStore.getActiveKid?.grade || null)
  const code = computed(() => isRouteDisciplineEla.value ? Discipline.ELA : Discipline.Math)

  const config = computed(() => grade.value && code.value ? configs[grade.value][code.value] : null)
  const title = computed(() => config.value?.title || null)
  const questions = computed(() => config.value?.questions || [])
  const questionsTotal = computed(() => questions.value.length)
  const currentQuestion = computed(() => questions.value.find( question => question.name === assessmentTestStore.getCurrentStep))

  const assessmentTitleCommon = computed(() => config.value?.quizTitle || null)
  const assessmentTitleCheckUp = computed(() => config.value?.quizTitleCheckUp || null)
  const assessmentTitle = computed(() => isComprehension ? assessmentTitleCheckUp.value : assessmentTitleCommon.value)

  const assessmentDescriptionCommon = computed(() => config.value?.description || null)
  const assessmentDescriptionCheckUp = computed(() => config.value?.descriptionCheckUp || null)
  const assessmentDescription = computed(() => isComprehension ? assessmentDescriptionCommon.value : assessmentDescriptionCheckUp.value)

  const homeStep = computed(() => questions.value[0]?.name || null)

  const initAssessmentTest = (): void => {
    assessmentTestStore.setComprehension(isComprehension)
    assessmentTestStore.setTitle(title.value)
    assessmentTestStore.setVariant( isRouteDisciplineEla.value ? Discipline.ELA : Discipline.Math)
    assessmentTestStore.setAssessmentTitle(assessmentTitle.value)
    assessmentTestStore.setAssessmentDescription(assessmentDescription.value)

    assessmentTestStore.setQuestions(questions.value)
    assessmentTestStore.setQuestionsTotal(questionsTotal.value)

    assessmentTestStore.setHomeStep(homeStep.value)
    assessmentTestStore.setCurrentStep( step.value ?? homeStep.value)
    assessmentTestStore.setCurrentQuestion(currentQuestion.value || null)

    if (answers.value) {
      const answersList = JSON.parse(answers.value )
      const questionName = currentQuestion.value?.name
      const currentAnswer = (questionName && answersList) ? answersList[currentQuestion.value?.name] : null

      assessmentTestStore.setAnswers(answersList)
      assessmentTestStore.setCurrentAnswer(currentAnswer)
    }
  }

  return {
    initAssessmentTest,
  }
}
